import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, FileSearch, Pencil, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { ActionConfirmationModal, Button } from "@/components/ui";
import { FileSearchResults } from "./FileSearchResults";
import { GlobalFileContextMenu } from "./GlobalFileContextMenu";
import { GlobalFileSearchLocation } from "./GlobalFileSearchLocation";
import { EditCanonicalFileModal } from "./EditCanonicalFileModal";
import {
  FileViewer,
  type FilePreviewVersion,
} from "@/components/features/matter/file-repo/FileViewer";
import { useFileSearch } from "@/hooks/use-file-search";
import { fileSearchMatterTitlesOptions } from "@/lib/data/react-query/files/search-files";
import { retrieveMassTortSearchQueryOptions } from "@/lib/data/react-query/mass-torts/mass-tort";
import { useDeleteCanonicalFile } from "@/lib/data/react-query/files/canonical-files";
import {
  downloadFile,
  downloadFileVersion,
  fileOperationMessage,
} from "@/lib/data/requests/files/canonical-files";
import type { CanonicalFile } from "@/lib/data/schemas/files";
import { downloadFileLink } from "@/lib/utils/matterFile";

export function GlobalFileSearchPanel({ query }: { query: string }) {
  const search = useFileSearch(query, { scope: "all" });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewVersion, setPreviewVersion] =
    useState<FilePreviewVersion | null>(null);
  const [editing, setEditing] = useState<CanonicalFile | null>(null);
  const [deleting, setDeleting] = useState<CanonicalFile | null>(null);
  const remove = useDeleteCanonicalFile();
  const files = search.hits.map((hit) => hit.file);
  const matterIds = [
    ...new Set(files.flatMap((file) => (file.matterId ? [file.matterId] : []))),
  ].sort();
  const matterTitles = useQuery(fileSearchMatterTitlesOptions(matterIds));
  const massTorts = useQuery({
    ...retrieveMassTortSearchQueryOptions(),
    enabled: files.some((file) => file.scope === "mass_tort"),
    staleTime: 60_000,
    retry: false,
  });
  const massTortTitles = Object.fromEntries(
    (massTorts.data?.data ?? []).map((item) => [
      item.id,
      item.title || "Mass-tort files",
    ]),
  );
  const location = (file: CanonicalFile) => (
    <GlobalFileSearchLocation
      file={file}
      matterTitles={matterTitles.data ?? {}}
      massTortTitles={massTortTitles}
    />
  );
  const selected = files.find((file) => file.id === selectedId);
  const download = async (file: CanonicalFile, versionId?: string | null) => {
    try {
      const result = versionId
        ? await downloadFileVersion(file.id, versionId)
        : await downloadFile(file.id);
      downloadFileLink(result.fileName, result.url);
    } catch (error) {
      toast.error(fileOperationMessage(error, "Unable to download this file."));
    }
  };
  return (
    <>
      <div className="border-border flex min-h-0 w-[360px] shrink-0 flex-col border-r xl:w-[420px]">
        <div className="border-b px-4 py-3 text-sm font-medium">
          Search results
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          <FileSearchResults
            search={search}
            files={files}
            selectedFileId={selectedId}
            onSelect={(file) => {
              if (file.id !== selectedId) setPreviewVersion(null);
              setSelectedId(file.id);
            }}
            renderLocation={(hit) => location(hit.file)}
            renderContextMenu={(file, trigger) => (
              <GlobalFileContextMenu
                item={file}
                trigger={trigger}
                canEdit={
                  file.scope !== "template" && file.capabilities.canRename
                }
                canDelete={
                  file.scope !== "template" && file.capabilities.canDelete
                }
                onEdit={() => {
                  setEditing(file);
                }}
                onDelete={() => {
                  setDeleting(file);
                }}
                onDownload={() => {
                  void download(file);
                }}
              />
            )}
          />
        </div>
      </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {selected ? (
          <>
            <div className="flex items-center gap-3 border-b px-6 py-4">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{selected.name}</p>
                <p className="text-muted-foreground truncate text-xs">
                  {location(selected)}
                </p>
              </div>
              {selected.scope !== "template" &&
                selected.capabilities.canRename && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Edit file"
                    onClick={() => {
                      setEditing(selected);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              {selected.capabilities.canDownload && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Download file"
                  onClick={() => {
                    void download(
                      selected,
                      previewVersion?.fileId === selected.id
                        ? previewVersion.versionId
                        : null,
                    );
                  }}
                >
                  <Download className="h-4 w-4" />
                </Button>
              )}
              {selected.scope !== "template" &&
                selected.capabilities.canDelete && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Delete file"
                    onClick={() => {
                      setDeleting(selected);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-6">
              {selected.capabilities.canDownload ? (
                <FileViewer
                  onVersionChange={setPreviewVersion}
                  file={{
                    fileId: selected.id,
                    fileName: selected.name,
                    presignedURLKey: "",
                  }}
                  fileType={
                    selected.scope === "firm" || selected.scope === "personal"
                      ? "firm-file"
                      : "matter-file"
                  }
                  showTitle={false}
                  showDownload={false}
                  wrapperClassName="h-full rounded-none shadow-none"
                />
              ) : (
                <p className="text-muted-foreground text-sm">
                  Preview unavailable for this file.
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-3 p-6 text-sm">
            <FileSearch className="h-8 w-8" />
            Select a file to preview.
          </div>
        )}
      </div>
      {editing && (
        <EditCanonicalFileModal
          key={editing.id}
          file={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
      <ActionConfirmationModal
        open={Boolean(deleting)}
        setOpen={(open) => {
          if (!open) setDeleting(null);
        }}
        title="Delete file"
        description={`Delete ${deleting?.name ?? "this file"}? This action cannot be undone.`}
        buttonText="Delete"
        isActionPending={remove.isPending}
        afterActionFn={async () => {
          if (!deleting) return;
          try {
            await remove.mutateAsync(deleting.id);
            setDeleting(null);
            toast.success("File deleted");
          } catch (error) {
            toast.error(
              fileOperationMessage(error, "Unable to delete this file."),
            );
          }
        }}
      />
    </>
  );
}
